/*
Created-by: XtroEdge AI
Updated-by: XtroEdge AI
Last modified: 2026-10-29T12:00:00+05:00
Intent: The Audit records card on a campaign's detail page (S1.3): every attempt on the campaign with its outcome, answers, verification method, source and confidence, and an Excel export of all of them. The server only answers for the signed-in business's own campaigns.
*/

import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { saveAs } from 'file-saver';
import { firstValueFrom } from 'rxjs';
import { PagesService } from '@services/pages.service';
import { SharedService } from '@services/shared.service';
import { isArabicLang } from 'src/app/utils/arabic-pdf.util';
import {
  AuditLang,
  AuditRecord,
  AuditSheetLabels,
  auditAnswerLines,
  auditExportFileName,
  auditLevelName,
  readAuditPage,
  writeAuditWorkbook,
} from 'src/app/utils/audit-export.util';

const KNOWN_STATUS = ['COMPLETED', 'FAILED', 'IN_PROGRESS', 'ABANDONED', 'EXPIRED'];
const KNOWN_METHOD = [
  'TEXT', 'QR_SCAN', 'LINK', 'TIMED_LINK', 'IMAGE_MATCH', 'GEO_POINTS', 'CLIENT_WEBHOOK',
  'PLATFORM_WEBHOOK', 'SURVEY', 'QUIZ', 'RECEIPT', 'REFERRAL',
];
const KNOWN_SOURCE = ['APP', 'WEB_QR', 'SDK'];
// The proof summary's keys (clientAuditRecords.ts proofSummary on the server).
const KNOWN_PROOF = [
  'answers', 'geo_points', 'geo_point_names', 'similarity_percent', 'image_sha256',
  'receipt_submission_id', 'file_sha256', 'client_confirmed', 'points_total',
  'referrals_completed', 'event_id', 'event_sha256',
];

@Component({
  selector: 'app-contest-audit-records',
  standalone: true,
  imports: [CommonModule, TranslateModule, NzTableModule, NzToolTipModule],
  templateUrl: './contest-audit-records.component.html',
  styleUrls: ['./contest-audit-records.component.scss'],
})
export class ContestAuditRecordsComponent implements OnChanges {
  @Input() contestId: string | null = null;
  @Input() campaignName = '';

  private pagesService = inject(PagesService);
  private sharedService = inject(SharedService);
  private translate = inject(TranslateService);

  records: AuditRecord[] = [];
  total = 0;
  page = 1;
  readonly perPage = 10;
  ledger = true;
  loading = false;
  loadFailed = false;
  exporting = false;
  expanded = new Set<string>();

  get lang(): AuditLang {
    return isArabicLang() ? 'ar' : 'en';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['contestId'] && this.contestId) this.load(1);
  }

  async load(page: number): Promise<void> {
    if (!this.contestId) return;
    this.loading = true;
    this.loadFailed = false;
    try {
      const response = await firstValueFrom(this.pagesService.create('CONTEST_AUDIT_RECORDS', {
        contest_id: this.contestId,
        page,
        per_page: this.perPage,
      }));
      const result = readAuditPage(response);
      this.records = result.items;
      this.total = result.total;
      this.page = page;
      this.ledger = result.ledger;
      this.expanded.clear();
    } catch (error: any) {
      if (error?.name === 'EmptyError') return;
      this.records = [];
      this.total = 0;
      this.loadFailed = true;
    } finally {
      this.loading = false;
    }
  }

  async export(): Promise<void> {
    if (!this.contestId || this.exporting) return;
    this.exporting = true;
    try {
      const response = await firstValueFrom(
        this.pagesService.create('CONTEST_AUDIT_EXPORT', { contest_id: this.contestId }));
      const result = readAuditPage(response);
      if (result.items.length === 0) {
        this.sharedService.createNotification('warning', this.translate.instant('auditRecords.empty'));
        return;
      }
      const buffer = await writeAuditWorkbook(result.items, this.lang, this.sheetLabels());
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, auditExportFileName(this.campaignName || String(this.contestId)));
      this.sharedService.createNotification(
        result.truncated ? 'warning' : 'success',
        this.translate.instant(result.truncated ? 'auditRecords.exportTruncated' : 'auditRecords.exported',
          { count: result.items.length }));
    } catch (error) {
      this.sharedService.showError(error, this.translate.instant('auditRecords.exportFailed'));
    } finally {
      this.exporting = false;
    }
  }

  toggle(record: AuditRecord, open: boolean): void {
    if (open) this.expanded.add(record.attempt_id);
    else this.expanded.delete(record.attempt_id);
  }

  trackRecord = (_: number, record: AuditRecord) => record.attempt_id;

  levelName(record: AuditRecord): string {
    return auditLevelName(record, this.lang)
      || `${this.translate.instant('contestDetails.levels.level')} ${record.level_position ?? ''}`.trim();
  }

  answerLines(record: AuditRecord): string[] {
    return auditAnswerLines(record, this.lang);
  }

  statusLabel(code: string): string {
    const key = String(code || '').toUpperCase();
    return KNOWN_STATUS.includes(key) ? this.translate.instant(`auditRecords.status.${key}`) : (code || '—');
  }

  statusTone(code: string): string {
    const key = String(code || '').toUpperCase();
    if (key === 'COMPLETED') return 'is-success';
    if (key === 'FAILED') return 'is-danger';
    return 'is-neutral';
  }

  methodLabel(code: string | null): string {
    const key = String(code || '').toUpperCase();
    if (!key) return '—';
    return KNOWN_METHOD.includes(key) ? this.translate.instant(`auditRecords.method.${key}`) : key;
  }

  sourceLabel(code: string | null): string {
    const key = String(code || '').toUpperCase();
    if (!key) return '—';
    return KNOWN_SOURCE.includes(key) ? this.translate.instant(`auditRecords.source.${key}`) : key;
  }

  withheldLabel(mode: string): string {
    return this.translate.instant(mode === 'aggregated' ? 'auditRecords.withheldAggregated' : 'auditRecords.withheldAnonymised');
  }

  confidenceText(value: number | null): string {
    return value === null || value === undefined ? '—' : `${Math.round(value * 100)}%`;
  }

  proofEntries(record: AuditRecord): { key: string; value: string }[] {
    return Object.entries(record.proof || {}).map(([key, value]) => ({
      key: KNOWN_PROOF.includes(key) ? this.translate.instant(`auditRecords.proofKeys.${key}`) : key,
      value: Array.isArray(value) ? value.join(', ') : String(value),
    }));
  }

  private sheetLabels(): AuditSheetLabels {
    const t = (key: string) => this.translate.instant(`auditRecords.sheet.${key}`);
    return {
      sheet: t('name'),
      headers: [
        'participantId', 'participant', 'phone', 'email', 'level', 'levelType', 'attempt', 'outcome',
        'reasonCode', 'reason', 'startedAt', 'submittedAt', 'method', 'source', 'confidence',
        'formula', 'verifiedAt', 'flagged', 'answers', 'proof',
      ].map(t),
      status: code => this.statusLabel(code),
      method: code => this.methodLabel(code),
      source: code => this.sourceLabel(code),
      withheld: mode => this.withheldLabel(mode),
      yes: this.translate.instant('auditRecords.yes'),
      no: this.translate.instant('auditRecords.no'),
    };
  }
}
